import { ErrorSeverity, ClientKind } from '@lingugeek/schemas';

/**
 * Super simple telemetry API for frontend apps
 */
class SimpleTelemetry {
  private endpoint: string = '';
  private token: string = '';
  private kind: ClientKind = ClientKind.Dashboard;
  private enabled: boolean = false;

  /**
   * Initialize telemetry - call once at app startup
   */
  init(options: {
    endpoint: string;
    token: string;
    kind: ClientKind;
    enabled?: boolean;
  }): void {
    this.endpoint = options.endpoint;
    this.token = options.token;
    this.kind = options.kind;
    this.enabled = options.enabled !== false;

    if (this.enabled && typeof window !== 'undefined') {
      this.setupGlobalHandlers();
    }
  }

  /**
   * Log an error - fire and forget, never throws
   */
  async logError(
    error: Error | string,
    userId?: string,
    extra?: Record<string, unknown>
  ): Promise<void> {
    if (!this.enabled) return;

    try {
      const message = error instanceof Error ? error.message : error;
      const stack = error instanceof Error ? error.stack : undefined;

      await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Telemetry-Token': this.token,
        },
        body: JSON.stringify({
          message: message.substring(0, 512),
          stack: stack?.substring(0, 16384),
          level: ErrorSeverity.Error,
          timestamp: new Date().toISOString(),
          userId: userId,
          metadata: {
            ...extra,
            kind: this.kind,
            release: window.__APP_RELEASE__ || 'unknown',
            environment: window.__APP_ENV__ || 'development',
          },
        }),
      });
    } catch {
      // Silently ignore all errors - telemetry should never break the app
    }
  }

  private setupGlobalHandlers(): void {
    // Catch unhandled errors
    window.addEventListener('error', (event: ErrorEvent) => {
      this.logError(event.error || event.message);
    });

    // Catch unhandled promise rejections
    window.addEventListener(
      'unhandledrejection',
      (event: PromiseRejectionEvent) => {
        this.logError(
          event.reason instanceof Error ? event.reason : String(event.reason)
        );
      }
    );

    // Intercept console.error for additional error tracking
    const originalConsoleError = console.error;
    console.error = (...args) => {
      originalConsoleError.apply(console, args);

      // Report to telemetry if it looks like an error
      const errorArg = args.find(arg => arg instanceof Error);
      if (errorArg) {
        this.logError(errorArg as Error, undefined, {
          source: 'console.error',
          consoleArgs: args.map(arg => String(arg)).slice(0, 3),
        });
      }
    };
  }
}

/**
 * Global telemetry instance
 */
export const telemetry = new SimpleTelemetry();
